/*
 *  Copyright © Microsoft Corporation. All rights reserved.
 */

import { useReactiveVar } from '@apollo/client';
import * as React from 'react';

import {
    blockMainActionVar,
    CreateTemplateInstanceWizardSteps,
    currentModeVar,
    currentStepVar,
    editingFormFocusCountVar,
    formsIframeSavingStatusVar,
    Mode,
    templateInstanceFormIdVar,
} from '../../../../../apollo/reactiveVariables/TemplateInstanceVariables';
import {
    currentScopeInCreationVar,
    currentTeamInCreationVar,
    currentTemplateVar,
    modalDataVar,
    ModalType,
    TemplateScope,
} from '../../../../../apollo/reactiveVariables/TemplateManagementVariables';
import formsAccountErrorImage from '../../../../../assets/FormsAccountNotEnabled.png';
import { LogCategory, ScenarioName, StatusName, StepName } from '../../../../../logger';
import { FormsIframeSavingStatus } from '../../../../../models/formsIframe/FormsIframe';
import {
    ApprovalTemplateInstanceScopeType,
    CreateApprovalFormClient,
    CreateApprovalFormOutput,
} from '../../../../../service/gen/templates';
import { getAppHost } from '../../../../../utilities/TeamsUtils';
import {
    ErrorEvent,
    FormsIframeErrorCode,
    FormsServiceErrorCode,
    getTemplateScenarioName,
    TemplateConstants,
    useIsMounted,
} from '../../../../../utilities/TemplateUtils';
import { isSelfOwnedFormEnabled } from '../../../../../utilities/Utils';
import { ErrorPageProps } from '../../../../containers/ErrorPage';
import { FormDesignComponent, FormDesignComponentProps } from './FormDesign.component';

export const FormDesignContainer: React.FC = (): JSX.Element => {
    const logger = getAppHost().getLogger();
    const [isLoading, setIsLoading] = React.useState<boolean>(true);
    const [retryCount, setRetryCount] = React.useState<number>(0);
    const [error, setError] = React.useState<ErrorPageProps>();
    const formsIframeSavingStatus = useReactiveVar(formsIframeSavingStatusVar);
    const currentStep = useReactiveVar(currentStepVar);
    const currentMode = useReactiveVar(currentModeVar);
    const editingFormFocusCount = useReactiveVar(editingFormFocusCountVar);
    const currentScope = useReactiveVar(currentScopeInCreationVar);
    const currentTeam = useReactiveVar(currentTeamInCreationVar);
    const currentTemplate = useReactiveVar(currentTemplateVar);
    const formId = useReactiveVar(templateInstanceFormIdVar);
    const formLoadStartTimeRef = React.useRef<number>();
    const modalData = useReactiveVar(modalDataVar);
    const timeoutSupervisor = React.useRef<ReturnType<typeof setTimeout>>();
    const isMounted = useIsMounted();

    const [showWarning, setShowWarning] = React.useState(false);

    const locale = getAppHost().getLocaleName();
    const loc = getAppHost().getLocale();

    const tryCreateForm = (): void => {
        if (currentMode === Mode.Create && !formId) {
            logger.logTraceInfo(LogCategory.TemplateInstanceWizard, 'Try to create new form');
            setIsLoading(true);
            setError(null);
            const startTime = performance.now();
            new CreateApprovalFormClient()
                .createApprovalForm({
                    Scope:
                        currentScope === TemplateScope.Team
                            ? ApprovalTemplateInstanceScopeType.Team
                            : ApprovalTemplateInstanceScopeType.Organization,
                    Locale: locale,
                    TeamId: currentTeam?.id,
                    TemplateId: currentTemplate?.id,
                })
                .then((value: CreateApprovalFormOutput): void => {
                    logger.markScenario(ScenarioName.CreateTemplateInstance, StepName.CreateForm, StatusName.Success, {
                        stepElapsed: performance.now() - startTime,
                    });

                    if (isMounted()) {
                        formLoadStartTimeRef.current = performance.now();
                        templateInstanceFormIdVar(value.Id);
                        setError(null);
                    }
                })
                .catch((error: any): any => {
                    logger.markScenario(ScenarioName.CreateTemplateInstance, StepName.CreateForm, StatusName.Fail, {
                        stepElapsed: performance.now() - startTime,
                    });

                    logger.logTraceHttpException('createApprovalForm', error?.message, error?.status);
                    const innerMessage = JSON.parse(error?.response)?.Properties?.innerMessage as string;
                    let errorMessage = null;
                    let shoudShowAdditionalMessage = false;
                    let additionalMessage = null;

                    // TODO: Refactor the following code after backend data structure is ready.
                    if (
                        innerMessage?.includes(FormsServiceErrorCode.GroupNotFound) ||
                        innerMessage?.includes(FormsServiceErrorCode.NotMemberOfGroup) ||
                        innerMessage?.includes(FormsServiceErrorCode.NotOwnerOfGroup) ||
                        innerMessage?.includes(FormsServiceErrorCode.NotTeamOwner)
                    ) {
                        errorMessage = loc.strings.onboard_forms_accounterror_title;
                        additionalMessage = loc.strings.onboard_forms_accounterror_tip;
                        shoudShowAdditionalMessage = true;
                    } else if (
                        innerMessage?.includes(FormsServiceErrorCode.LicenseNotFound) ||
                        innerMessage?.includes(FormsServiceErrorCode.LicenseExpired)
                    ) {
                        errorMessage = loc.strings.forms_license_error;
                    } else if (innerMessage?.includes(FormsServiceErrorCode.ReachFormsLimit)) {
                        errorMessage = loc.strings.approval_form_reach_limitation_title;
                        additionalMessage = loc.strings.approval_form_reach_limitation_desc;
                        shoudShowAdditionalMessage = true;
                    }

                    setError({
                        onAction: tryCreateForm,
                        message: errorMessage,
                        additionalMessage: shoudShowAdditionalMessage ? additionalMessage : null,
                        image: formsAccountErrorImage,
                    });
                    setIsLoading(false);
                });
        } else {
            formLoadStartTimeRef.current = performance.now();
        }
    };

    const onWarningClosed = (): void => {
        setShowWarning(false);
        editingFormFocusCountVar(editingFormFocusCount + 1);
    };

    React.useEffect((): void => {
        // Only show warning when first time focusing on form in edit mode
        if (currentMode === Mode.Edit && editingFormFocusCount === 1) {
            setShowWarning(true);
        } else {
            setShowWarning(false);
        }
    }, [currentMode, editingFormFocusCount]);

    React.useEffect((): void => {
        if (isSelfOwnedFormEnabled()) return;
        tryCreateForm();
    }, []);

    React.useEffect((): any => {
        if (isLoading) {
            timeoutSupervisor.current = setTimeout((): void => {
                logger.markScenario(
                    getTemplateScenarioName(currentMode),
                    StepName.FormsDesignLoadFailed,
                    StatusName.NA,
                    {
                        stepElapsed: formLoadStartTimeRef.current && performance.now() - formLoadStartTimeRef.current,
                        data: {
                            code: FormsIframeErrorCode.LoadTimeout,
                            isRetriable: false,
                            message: 'Forms iframe loaded timeout',
                            formId,
                            errorCode: FormsIframeErrorCode.LoadTimeout,
                        },
                    }
                );
            }, TemplateConstants.commonIframeTimeout);
        } else {
            clearTimeout(timeoutSupervisor.current);
        }

        return (): void => clearTimeout(timeoutSupervisor.current);
    }, [isLoading]);

    React.useEffect((): void => {
        if (currentStep === CreateTemplateInstanceWizardSteps.FormDesign) {
            if (
                currentMode === Mode.Create &&
                (error || isLoading || formsIframeSavingStatus !== FormsIframeSavingStatus.Saved)
            ) {
                blockMainActionVar(true);
            } else {
                blockMainActionVar(false);
            }
        }
    }, [currentStep, formsIframeSavingStatus, isLoading, error, currentMode]);

    const onSavingStatusChange = (status: FormsIframeSavingStatus): void => {
        formsIframeSavingStatusVar(status);
    };

    const onFormFocus = (): void => {
        if (currentMode === Mode.Edit) {
            editingFormFocusCountVar(editingFormFocusCount + 1);
        }
    };

    const onFormReady = (): void => {
        if (modalData.type !== ModalType.PreviewTemplateInstance) {
            logger.logTraceInfo(LogCategory.TemplateInstanceWizard, 'Form design time iframe is ready');
            logger.markScenario(getTemplateScenarioName(currentMode), StepName.FormsDesignReady, StatusName.NA, {
                stepElapsed: formLoadStartTimeRef.current && performance.now() - formLoadStartTimeRef.current,
                data: {
                    formId,
                },
            });
        }

        setIsLoading(false);
    };

    const onFormError = (event: ErrorEvent): void => {
        logger.logTraceError(
            LogCategory.TemplateInstanceWizard,
            `Form design time iframe posted error with code:${event.code}, isRetriable: ${event.isRetriable}, message: ${event.message}`
        );

        logger.markScenario(getTemplateScenarioName(currentMode), StepName.FormsDesignLoadFailed, StatusName.NA, {
            stepElapsed: formLoadStartTimeRef.current && performance.now() - formLoadStartTimeRef.current,
            data: {
                code: event.code,
                isRetriable: event.isRetriable,
                message: event.message,
                formId,
                errorCode: event.code,
            },
        });

        // We display more specific error message for some known issue scenarios
        let errorMessage;
        let shoudShowAdditionalMessage = false;
        let additionalMessage = null;
        if (
            event.code === FormsServiceErrorCode.GroupNotFound ||
            event.code === FormsServiceErrorCode.NotMemberOfGroup ||
            event.code === FormsServiceErrorCode.NotOwnerOfGroup ||
            event.code === FormsServiceErrorCode.NotTeamOwner
        ) {
            errorMessage = loc.strings.onboard_forms_accounterror_title;
            additionalMessage = loc.strings.onboard_forms_accounterror_tip;
            shoudShowAdditionalMessage = true;
        } else if (event.code === FormsIframeErrorCode.FormIsDeleted) {
            errorMessage = loc.strings.template_designtime_error_form_deleted;
        } else if (event.code === FormsServiceErrorCode.ReachFormsLimit) {
            errorMessage = loc.strings.approval_form_reach_limitation_title;
            additionalMessage = loc.strings.approval_form_reach_limitation_desc;
            shoudShowAdditionalMessage = true;
        } else if (
            event.code === FormsServiceErrorCode.LicenseNotFound ||
            event.code === FormsServiceErrorCode.LicenseExpired
        ) {
            errorMessage = loc.strings.forms_license_error;
        }

        setIsLoading(false);
        setError({
            message: errorMessage,
            additionalMessage: shoudShowAdditionalMessage ? additionalMessage : null,
            image: shoudShowAdditionalMessage ? formsAccountErrorImage : null,
            onAction:
                event.isRetriable &&
                ((): void => {
                    formLoadStartTimeRef.current = performance.now();
                    setRetryCount(retryCount + 1);
                    setIsLoading(true);
                    setError(null);
                }),
        });
    };

    const componentProps: FormDesignComponentProps = {
        isLoading,
        setIsLoading,
        onFormReady,
        error,
        setError,
        onFormError,
        onSavingStatusChange,
        onFormFocus,
        formId,
        showWarning,
        onWarningClosed,
        retryCount,
        isSelfOwnedFormEnabled: isSelfOwnedFormEnabled(),
    };

    return <FormDesignComponent {...componentProps} />;
};
